import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Stethoscope, UserPlus } from 'lucide-react';
import { authAPI } from '../../api';
import MD5 from 'crypto-js/md5';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';

interface RegisterFormInput {
  businessName?: string;
  contactPerson?: string;
  phone?: string;
  email?: string;
  password?: string;
}

export default function RegisterPage() {
  const { register, handleSubmit, watch, setValue, setError, formState: { errors } } = useForm<RegisterFormInput>();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>, field: any) => {
    const val = e.target.value;
    if (val.length > 0) {
      const capitalized = val.charAt(0).toUpperCase() + val.slice(1);
      setValue(field, capitalized, { shouldValidate: true, shouldDirty: true });
    }
  };



  const onSubmit = async (data: any) => {
    setLoading(true);
    try {
      const payload = { ...data, email: data.email?.toLowerCase(), password: MD5(data.password).toString() };
      const res = await authAPI.register(payload);
      if (res.data?.response === 'S') {
        toast.success('Basic details captured! Set your store location.');
        navigate('/location', { state: { registrationData: payload } });
      } else if (res.data?.response === 'E') {
        setError('email', { type: 'manual', message: res.data.message });
      } else if (res.data?.response === 'P') {
        setError('phone', { type: 'manual', message: res.data.message });
      } else {
        Swal.fire('Error', res.data?.message || 'Registration failed', 'error');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message?.toLowerCase() || '';
      if (errorMessage.includes('email')) {
        setError('email', { type: 'manual', message: err.response.data.message });
      } else if (errorMessage.includes('phone') || errorMessage.includes('mobile')) {
        setError('phone', { type: 'manual', message: err.response.data.message });
      } else {
        Swal.fire('Error', err.response?.data?.message || 'Registration failed', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      padding: '24px', minHeight: '100vh',
      background: 'linear-gradient(135deg, #f0fdfa 0%, #e0f2fe 40%, #faf5ff 80%, #fff7ed 100%)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
    }}>
      <div style={{ width: '100%', maxWidth: '480px', animation: 'fadeIn 0.5s ease-out' }}>
        {/* Brand */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            width: '64px', height: '64px', borderRadius: '18px',
            background: 'linear-gradient(135deg, #14b8a6, #0d9488)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px', boxShadow: '0 12px 32px rgba(20,184,166,0.3)',
            border: '2px solid rgba(255,255,255,0.6)'
          }}>
            <Stethoscope size={32} color="white" />
          </div>
          <h1 style={{ color: '#0f172a', fontSize: '28px', fontWeight: '800', letterSpacing: '-0.5px' }}>
            Decso<span style={{ color: '#14b8a6' }}>plus</span> Seller Registration
          </h1>
        </div>

        {/* Card */}
        <div style={{
          background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(10px)',
          borderRadius: '24px', padding: '40px',
          boxShadow: '0 20px 40px rgba(0,0,0,0.08)',
          border: '1px solid rgba(255,255,255,0.6)'
        }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              <div>
                <label className="form-label" style={{ color: '#475569', fontWeight: '600', fontSize: '13px' }}>Business Name <span style={{ fontSize: '12px', color: '#d31616ff', marginLeft: '2px' }}>*</span></label>
                <input className="form-input" placeholder="e.g. Dr Mehta Supplies"
                  style={{ height: '48px', border: '1px solid #cbd5e1', background: '#f8fafc', color: '#0f172a', textTransform: 'capitalize' }}
                  {...register('businessName', {
                    required: 'Business name is required',
                    onChange: (e) => handleNameChange(e, 'businessName'),
                    pattern: {
                      value: /^[A-Z][a-zA-Z\s]*$/,
                      message: 'Start with capital letter. No numbers or special chars.'
                    }
                  })} />
                {errors.businessName && <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', fontWeight: '500' }}>{errors.businessName.message}</p>}
              </div>

              <div>
                <label className="form-label" style={{ color: '#475569', fontWeight: '600', fontSize: '13px' }}>Contact Person <span style={{ fontSize: '12px', color: '#d31616ff', marginLeft: '2px' }}>*</span></label>
                <input className="form-input" placeholder="e.g. John Doe"
                  style={{ height: '48px', border: '1px solid #cbd5e1', background: '#f8fafc', color: '#0f172a', textTransform: 'capitalize' }}
                  {...register('contactPerson', {
                    required: 'Contact person is required',
                    onChange: (e) => handleNameChange(e, 'contactPerson'),
                    pattern: {
                      value: /^[A-Z][a-zA-Z\s]*$/,
                      message: 'Start with capital letter. No numbers or special chars.'
                    }
                  })} />
                {errors.contactPerson && <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', fontWeight: '500' }}>{errors.contactPerson.message}</p>}
              </div>

              <div>
                <label className="form-label" style={{ color: '#475569', fontWeight: '600', fontSize: '13px' }}>Phone Number <span style={{ fontSize: '12px', color: '#d31616ff', marginLeft: '2px' }}>*</span></label>
                <input className="form-input" placeholder="10-digit mobile number"
                  style={{ height: '48px', border: '1px solid #cbd5e1', background: '#f8fafc', color: '#0f172a' }}
                  maxLength={10}
                  {...register('phone', {
                    required: 'Phone number is required',
                    pattern: { value: /^[0-9]{10}$/, message: 'Must be exactly 10 digits' }
                  })} />
                {errors.phone && <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', fontWeight: '500' }}>{errors.phone.message}</p>}
              </div>


              <div>
                <label className="form-label" style={{ color: '#475569', fontWeight: '600', fontSize: '13px' }}>Email ID <span style={{ fontSize: '12px', color: '#d31616ff', marginLeft: '2px' }}>*</span></label>
                <input type="email" className="form-input" placeholder="seller@store.com"
                  style={{ height: '48px', border: '1px solid #cbd5e1', background: '#f8fafc', color: '#0f172a' }}
                  {...register('email', {
                    required: 'Email is required',
                    pattern: { value: /^\S+@\S+$/i, message: 'Invalid email format' }
                  })} />
                {errors.email && <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', fontWeight: '500' }}>{errors.email.message}</p>}
              </div>

              <div>
                <label className="form-label" style={{ color: '#475569', fontWeight: '600', fontSize: '13px' }}>Password <span style={{ fontSize: '12px', color: '#d31616ff', marginLeft: '2px' }}>*</span></label>
                <div style={{ position: 'relative' }}>
                  <input type={showPassword ? 'text' : 'password'} className="form-input" placeholder="Min. 8 characters"
                    style={{ paddingRight: '48px', height: '48px', border: '1px solid #cbd5e1', background: '#f8fafc', color: '#0f172a' }}
                    {...register('password', {
                      required: 'Password required',
                      minLength: {
                        value: 8,
                        message: 'Minimum 8 characters'
                      }
                    })} />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
                    {showPassword ?  <Eye size={20} color="#94a3b8" /> : <EyeOff size={20} color="#94a3b8" />}
                  </button>
                </div>


                {errors.password && <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', fontWeight: '500' }}>{errors.password.message}</p>}
              </div>
            </div>

            <button type="submit" className="btn-primary" style={{ width: '100%', marginTop: '32px', justifyContent: 'center', height: '52px', fontSize: '16px', borderRadius: '12px', background: 'linear-gradient(135deg, #14b8a6, #0d9488)', border: 'none', color: 'white', fontWeight: '700', boxShadow: '0 8px 20px rgba(20,184,166,0.25)' }} disabled={loading}>
              {loading ? <div className="spinner" style={{ width: '20px', height: '20px' }} /> : <><UserPlus size={20} /> Create Account</>}
            </button>
          </form>

          <p style={{ textAlign: 'center', marginTop: '24px', fontSize: '14px', color: '#64748b', fontWeight: '500' }}>
            Already registered?{' '}
            <Link to="/login" style={{ color: '#14b8a6', fontWeight: '800', textDecoration: 'none' }}>Sign In</Link>
          </p>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
